import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../contexts/AuthContext";
import { Button } from "@/shared/ui";
import { supabase } from "@/shared/db";
import {
  getUserCredits,
  purchaseCredits,
  CREDIT_PACKAGES,
  type CreditPackage,
} from "@/shared/db";
import { toast } from "sonner";
import { X, Shield, Loader2, Sparkles, CreditCard } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import type { StripeElementsOptions } from "@stripe/stripe-js";
import { CheckoutForm } from "./CheckoutForm";

// Load Stripe
const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLIC_KEY || "");

interface BuyCreditsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export const BuyCreditsModal: React.FC<BuyCreditsModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [currentCredits, setCurrentCredits] = useState<number>(0);
  const [selectedPackage, setSelectedPackage] = useState<CreditPackage | null>(
    null
  );
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [paymentIntentId, setPaymentIntentId] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && user) {
      loadCurrentCredits();
    }
  }, [isOpen, user]);

  // Reset when modal closes
  useEffect(() => {
    if (!isOpen) {
      setClientSecret(null);
      setPaymentIntentId(null);
      setProcessing(false);
      setSelectedPackage(null);
    }
  }, [isOpen]);

  const loadCurrentCredits = async () => {
    if (!user) return;

    try {
      setLoading(true);
      const credits = await getUserCredits(user.id);
      setCurrentCredits(credits?.credits_balance || 0);
    } catch (error) {
      console.error("Error loading credits:", error);
    } finally {
      setLoading(false);
    }
  };

  const initializePayment = async (creditPackage: CreditPackage) => {
    if (!user) return;

    setProcessing(true);
    setSelectedPackage(creditPackage);

    try {
      // Refresh session to ensure we have a valid token (fixes 401 on expired tokens)
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      if (sessionError || !session) {
        throw new Error("No active session");
      }
      // If token expires in less than 60s, refresh it
      const expiresAt = session.expires_at;
      if (expiresAt && expiresAt - Math.floor(Date.now() / 1000) < 60) {
        await supabase.auth.refreshSession();
      }

      // Call Edge Function via Supabase client - handles auth headers & token refresh
      const { data: responseData, error } = await supabase.functions.invoke(
        "purchase-credits",
        {
          method: "POST",
          body: {
            userId: user.id,
            credits: creditPackage.credits,
            amount: creditPackage.price,
          },
        }
      );

      if (error) {
        const errorMessage =
          (responseData && typeof responseData === "object" && (responseData as { error?: string })?.error) ||
          (responseData && typeof responseData === "object" && (responseData as { details?: string })?.details) ||
          error.message ||
          "Error initializing payment";
        console.error("Edge Function error:", { error, responseData });
        throw new Error(
          error.message?.includes("401") || error.message?.toLowerCase().includes("unauthorized")
            ? t("payments.checkout.sessionExpired")
            : errorMessage
        );
      }

      const parsed = (responseData && typeof responseData === "object" ? responseData : {}) as { clientSecret?: string; paymentIntentId?: string };
      if (!parsed?.clientSecret) {
        throw new Error("No client secret returned");
      }

      setClientSecret(parsed.clientSecret);
      setPaymentIntentId(parsed.paymentIntentId ?? null);
    } catch (error: any) {
      console.error("Error initializing payment:", error);
      const msg = error.message || "";
      const errorMessage =
        msg.toLowerCase().includes("trial") || msg.includes("prueba")
          ? t("payments.checkout.trialingNoPurchase")
          : msg || t("payments.checkout.initPaymentError");

      toast.error(errorMessage, {
        duration: 5000,
      });
      setSelectedPackage(null);
    } finally {
      setProcessing(false);
    }
  };

  const handlePaymentSuccess = async () => {
    toast.success(t("payments.checkout.buyCredits.creditsPurchased"));

    // Client-side fallback: add credits immediately (webhook may not fire)
    // add_credits is idempotent - safe if webhook also runs
    if (user && selectedPackage && paymentIntentId) {
      try {
        await purchaseCredits(user.id, selectedPackage.credits, paymentIntentId);
      } catch (err) {
        console.warn("Client-side credit add failed (webhook may have run):", err);
      }
    }

    const delay = (ms: number) => new Promise((r) => setTimeout(r, ms));
    await delay(500);

    for (let i = 0; i < 3; i++) {
      const credits = (await getUserCredits(user!.id))?.credits_balance ?? 0;
      if (credits > 0) break;
      await delay(1000);
    }

    await loadCurrentCredits();
    if (onSuccess) {
      onSuccess();
    }
    onClose();
  };

  const handlePaymentError = (error: string) => {
    toast.error(error);
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
            onClick={onClose}
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-white rounded-xl shadow-2xl max-w-[calc(100vw-2rem)] sm:max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-tiber-200">
                <div>
                  <h2 className="text-2xl font-bold text-tiber-900 flex items-center gap-2">
                    <Sparkles className="w-6 h-6 text-tiber-500" />
                    {t("payments.checkout.buyCredits.title")}
                  </h2>
                  <p className="text-sm text-tiber-600 mt-1">
                    {t("payments.checkout.buyCredits.subtitle")}
                  </p>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-romance-50 rounded-lg transition-colors"
                  aria-label="Close"
                >
                  <X className="w-6 h-6 text-tiber-600" />
                </button>
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-6">
                {loading ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 text-tiber-500 animate-spin" />
                  </div>
                ) : (
                  <div className="space-y-6">
                    {/* Current Credits */}
                    <div className="bg-linear-to-br from-tiber-500/10 to-tiber-50 rounded-xl p-6 border border-tiber-500/20">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-tiber-600 mb-1">
                            {t("payments.checkout.buyCredits.currentCredits")}
                          </p>
                          <p className="text-3xl font-bold text-tiber-900">
                            {currentCredits}
                          </p>
                        </div>
                        <div className="p-4 bg-linear-to-br from-tiber-500 to-tiber-600 rounded-xl">
                          <Sparkles className="w-8 h-8 text-white" />
                        </div>
                      </div>
                    </div>

                    {/* Credit Packages */}
                    {!clientSecret ? (
                      <div>
                        <h3 className="text-lg font-bold text-tiber-900 mb-4">
                          {t("payments.checkout.buyCredits.creditPackages")}
                        </h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {CREDIT_PACKAGES.map((pkg) => {
                            const pricePerCredit = pkg.price / pkg.credits;
                            const savings =
                              pkg.id === "25"
                                ? "10%"
                                : pkg.id === "50"
                                  ? "20%"
                                  : pkg.id === "100"
                                    ? "27%"
                                    : null;

                            return (
                              <motion.div
                                key={pkg.id}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                className={`relative bg-white rounded-xl border-2 transition-all duration-300 hover:shadow-lg ${
                                  pkg.popular
                                    ? "border-tiber-500 scale-105"
                                    : "border-tiber-200 hover:border-tiber-500/50"
                                }`}
                              >
                                {/* Popular Badge */}
                                {pkg.popular && (
                                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 z-10">
                                    <span className="bg-linear-to-r from-tiber-500 to-tiber-600 text-white px-4 py-1 rounded-full text-xs font-semibold shadow-lg">
                                      {t(
                                        "payments.checkout.buyCredits.mostPopular"
                                      )}
                                    </span>
                                  </div>
                                )}

                                <div className="p-6">
                                  <div className="text-center mb-4">
                                    <h4 className="text-2xl font-bold text-tiber-900 mb-2">
                                      {pkg.credits}{" "}
                                      {t(
                                        "payments.checkout.buyCredits.credits"
                                      )}
                                    </h4>
                                    <div className="flex items-center justify-center gap-2">
                                      <span className="text-3xl font-bold text-tiber-500">
                                        ${pkg.price.toFixed(2)}
                                      </span>
                                      {savings && (
                                        <span className="px-2 py-1 bg-atlantis-100 text-atlantis-800 text-xs font-semibold rounded-full">
                                          {t(
                                            "payments.checkout.buyCredits.save",
                                            { savings }
                                          )}
                                        </span>
                                      )}
                                    </div>
                                    <p className="text-sm text-tiber-600 mt-1">
                                      ${pricePerCredit.toFixed(2)}{" "}
                                      {t(
                                        "payments.checkout.buyCredits.perCredit"
                                      )}
                                    </p>
                                  </div>

                                  <Button
                                    variant={
                                      pkg.popular ? "primary" : "secondary"
                                    }
                                    size="sm"
                                    className="w-full"
                                    onClick={() => initializePayment(pkg)}
                                    disabled={processing}
                                    isLoading={
                                      processing &&
                                      selectedPackage?.id === pkg.id
                                    }
                                    leftIcon={
                                      <CreditCard className="w-4 h-4" />
                                    }
                                  >
                                    {t("payments.checkout.buyCredits.purchase")}
                                  </Button>
                                </div>
                              </motion.div>
                            );
                          })}
                        </div>

                        {/* Info */}
                        <div className="mt-6 bg-romance-50 rounded-lg p-4 border border-tiber-200">
                          <div className="flex items-start gap-3">
                            <Shield className="w-5 h-5 text-atlantis-600 mt-0.5 shrink-0" />
                            <div>
                              <p className="text-sm font-medium text-tiber-900 mb-1">
                                {t(
                                  "payments.checkout.buyCredits.howCreditsWork"
                                )}
                              </p>
                              <ul className="text-xs text-tiber-600 space-y-1">
                                <li>
                                  •{" "}
                                  {t(
                                    "payments.checkout.buyCredits.creditsDescription1"
                                  )}
                                </li>
                                <li>
                                  •{" "}
                                  {t(
                                    "payments.checkout.buyCredits.creditsDescription2"
                                  )}
                                </li>
                                <li>
                                  •{" "}
                                  {t(
                                    "payments.checkout.buyCredits.creditsDescription3"
                                  )}
                                </li>
                                <li>
                                  •{" "}
                                  {t(
                                    "payments.checkout.buyCredits.creditsDescription4"
                                  )}
                                </li>
                              </ul>
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      /* Payment Form */
                      <div className="bg-white border-2 border-tiber-200 rounded-xl p-6">
                        <h3 className="text-lg font-bold text-tiber-900 mb-4">
                          {t("payments.checkout.buyCredits.completePayment")}
                        </h3>

                        {selectedPackage && (
                          <div className="mb-4 p-4 bg-romance-50 rounded-lg">
                            <div className="flex justify-between items-center mb-2">
                              <span className="text-tiber-600">
                                {selectedPackage.credits}{" "}
                                {t("payments.checkout.buyCredits.credits")}
                              </span>
                              <span className="font-semibold text-tiber-900">
                                ${selectedPackage.price.toFixed(2)}
                              </span>
                            </div>
                            <div className="border-t border-tiber-200 pt-3">
                              <div className="flex justify-between items-center">
                                <span className="text-lg font-bold text-tiber-900">
                                  {t("payments.checkout.buyCredits.total")}
                                </span>
                                <span className="text-lg font-bold text-tiber-500">
                                  ${selectedPackage.price.toFixed(2)}
                                </span>
                              </div>
                            </div>
                          </div>
                        )}

                        {processing && !clientSecret ? (
                          <div className="text-center py-8">
                            <Loader2 className="w-8 h-8 text-tiber-500 animate-spin mx-auto mb-4" />
                            <p className="text-sm text-tiber-600">
                              {t("payments.checkout.initializingPayment")}
                            </p>
                          </div>
                        ) : clientSecret ? (
                          <Elements
                            stripe={stripePromise}
                            options={
                              {
                                clientSecret,
                                appearance: {
                                  theme: "stripe",
                                  variables: {
                                    colorPrimary: "#1cb0a9",
                                    colorBackground: "#ffffff",
                                    colorText: "#1f2937",
                                    colorDanger: "#ef4444",
                                    fontFamily: "system-ui, sans-serif",
                                    spacingUnit: "4px",
                                    borderRadius: "8px",
                                  },
                                },
                              } as StripeElementsOptions
                            }
                          >
                            <CheckoutForm
                              clientSecret={clientSecret}
                              onSuccess={handlePaymentSuccess}
                              onError={handlePaymentError}
                            />
                          </Elements>
                        ) : null}

                        <p className="text-xs text-center text-tiber-500 mt-4">
                          {t("payments.checkout.termsAgreement")}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};
